import React, { useState, useEffect } from 'react';
import { getEndpointHistory } from '../../../shared/api/publicApi';
import '../../../styles/features/status/UptimeBar.css';

function barLevel(uptime) {
  if (uptime == null) return 'empty';
  if (uptime >= 99) return 'up';
  if (uptime >= 95) return 'degraded';
  return 'down';
}

// The server groups checks by UTC date, so the day list is built in UTC too.
function lastDaysUtc(days) {
  const today = new Date();
  const list = [];
  for (let i = days - 1; i >= 0; i -= 1) {
    const d = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - i));
    list.push(d.toISOString().slice(0, 10));
  }
  return list;
}

function formatDay(dateStr) {
  return new Date(`${dateStr}T00:00:00Z`).toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', timeZone: 'UTC',
  });
}

function UptimeBar({ endpointId, days = 90 }) {
  const [byDate, setByDate] = useState(null);
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    if (!endpointId) return undefined;
    let cancelled = false;
    getEndpointHistory(endpointId, days)
      .then((res) => {
        if (cancelled) return;
        const map = {};
        for (const row of res.data?.data || []) map[row.date] = row;
        setByDate(map);
      })
      .catch(() => { if (!cancelled) setByDate({}); });
    return () => { cancelled = true; };
  }, [endpointId, days]);

  if (!byDate) return <div className="uptime-bar_loading">Loading history…</div>;

  const dates = lastDaysUtc(days);
  const recorded = dates.map((d) => byDate[d]).filter(Boolean);
  const totalChecks = recorded.reduce((sum, row) => sum + (row.checks || 0), 0);
  const weightedUp = recorded.reduce((sum, row) => sum + ((row.uptime ?? 0) * (row.checks || 0)), 0);
  const periodUptime = totalChecks > 0 ? weightedUp / totalChecks : null;

  const hoveredRow = hovered ? byDate[hovered] : null;

  return (
    <div className="uptime-bar">
      <div className="uptime-bar_header">
        <h3 className="section-title">{days}-Day History</h3>
        {periodUptime != null && (
          <span className="uptime-bar_period">{periodUptime.toFixed(2)}% uptime</span>
        )}
      </div>

      <div className="uptime-bar_track" onMouseLeave={() => setHovered(null)}>
        {dates.map((date) => {
          const row = byDate[date];
          return (
            <span
              key={date}
              className={`uptime-bar_day uptime-bar_day--${barLevel(row?.uptime)} ${hovered === date ? 'is-hovered' : ''}`}
              onMouseEnter={() => setHovered(date)}
              aria-label={row ? `${formatDay(date)}: ${row.uptime}% uptime` : `${formatDay(date)}: no data`}
            />
          );
        })}
      </div>

      <div className="uptime-bar_footer">
        <span>{days} days ago</span>
        <span className="uptime-bar_detail">
          {hovered
            ? (hoveredRow
              ? `${formatDay(hovered)} · ${hoveredRow.uptime}% uptime · ${hoveredRow.checks} checks`
              : `${formatDay(hovered)} · no data`)
            : 'Hover a day for details'}
        </span>
        <span>Today</span>
      </div>
    </div>
  );
}

export default UptimeBar;
